import { useNavigate } from 'react-router-dom';
import { usePlayerV2 } from '../stores/usePlayerV2';
import './NowPlayingBarV2.css';

function fmtTime(sec: number): string {
  if (!isFinite(sec) || sec < 0) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function NowPlayingBarV2() {
  const status = usePlayerV2((s) => s.status);
  const title = usePlayerV2((s) => s.title);
  const pos = usePlayerV2((s) => s.positionSec);
  const dur = usePlayerV2((s) => s.durationSec);
  const fp = usePlayerV2((s) => s.fetchProgress);
  const error = usePlayerV2((s) => s.error);
  const pause = usePlayerV2((s) => s.pause);
  const resume = usePlayerV2((s) => s.resume);
  const stop = usePlayerV2((s) => s.stop);
  const nav = useNavigate();

  if (status === 'idle' || !title) return null;

  const preplay =
    status === 'resolving' || status === 'fetching' || status === 'buffering';
  const playing = status === 'playing';

  let sub: string;
  let subClass = 'nowplay2-sub';
  if (status === 'error') {
    sub = error ?? 'Error';
    subClass += ' error';
  } else if (status === 'resolving') {
    sub = 'Loading…';
  } else if (status === 'fetching') {
    if (fp && fp.total) sub = `${fmtBytes(fp.loaded)} / ${fmtBytes(fp.total)}`;
    else if (fp) sub = `${fmtBytes(fp.loaded)} loaded`;
    else sub = 'Downloading…';
  } else if (status === 'buffering' && pos === 0) {
    sub = 'Buffering…';
  } else if (status === 'ended') {
    sub = 'Done';
  } else {
    sub = `${fmtTime(pos)} / ${fmtTime(dur)}`;
  }

  let pct: number | null = null;
  let indeterminate = false;
  if (status === 'fetching' && fp && fp.total) {
    pct = Math.min(100, (fp.loaded / fp.total) * 100);
  } else if (status === 'resolving' || status === 'buffering' || status === 'fetching') {
    indeterminate = true;
  }

  return (
    <div className="nowplay2" role="region" aria-label="Now playing">
      <div className="nowplay2-progress" aria-hidden="true">
        {pct != null && (
          <div className="nowplay2-progress-fill" style={{ width: `${pct}%` }} />
        )}
        {indeterminate && pct == null && (
          <div className="nowplay2-progress-fill indeterminate" />
        )}
      </div>

      <button
        className="nowplay2-info"
        onClick={() => nav('/player')}
        aria-label="Open player"
      >
        <div className="nowplay2-title">{title}</div>
        <div className={subClass}>{sub}</div>
      </button>

      <button
        className="nowplay2-toggle"
        disabled={preplay}
        onClick={() => {
          if (playing) pause();
          else void resume();
        }}
        aria-label={
          preplay ? 'Loading' : playing ? 'Pause' : 'Play'
        }
      >
        {preplay ? (
          <span className="nowplay2-spinner" aria-hidden="true" />
        ) : playing ? (
          <span aria-hidden="true">❚❚</span>
        ) : (
          <span aria-hidden="true">▶</span>
        )}
      </button>

      <button
        className="nowplay2-close"
        onClick={stop}
        aria-label="Stop"
        title="Stop"
      >
        ×
      </button>
    </div>
  );
}
